import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp,FileSpreadsheet, FileText } from 'lucide-react';
import LeadModal  from './FloatingElements'; // ייבוא הקומפוננטה
import api from '../services/api';
import { getDisplayName } from '../config/fundNamesConfig';
import { getSpecializationConfigs } from '../config/fundConfigurations';
import { saveContact } from '../services/firebase/contactService';
import html2canvas from 'html2canvas';
// הקומפוננטות הקיימות נשארות אותו דבר
const TableSkeleton = ({ title }) => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3">
      <h3 className="font-bold text-sm">{title}</h3>
      <div className="text-xs text-blue-100 mt-1">
        <div className="animate-pulse bg-blue-400/50 rounded h-3 w-48"></div>
      </div>
    </div>

    <div className="p-4">
      <div className="space-y-3">
        <div className="flex justify-between items-center border-b pb-2">
          <div className="animate-pulse bg-gray-200 rounded h-4 w-12"></div>
          <div className="animate-pulse bg-gray-200 rounded h-4 w-24"></div>
          <div className="animate-pulse bg-gray-200 rounded h-4 w-16"></div>
          <div className="animate-pulse bg-gray-200 rounded h-4 w-16"></div>
          <div className="animate-pulse bg-gray-200 rounded h-4 w-16"></div>
          <div className="animate-pulse bg-gray-200 rounded h-4 w-16"></div>
          <div className="animate-pulse bg-gray-200 rounded h-4 w-16"></div>
        </div>

        {[...Array(8)].map((_, i) => (
          <div key={i} className="flex justify-between items-center py-2">
            <div className="animate-pulse bg-gray-200 rounded h-3 w-8"></div>
            <div className="animate-pulse bg-gray-200 rounded h-3 w-28"></div>
            <div className="animate-pulse bg-gray-200 rounded h-3 w-12"></div>
            <div className="animate-pulse bg-gray-200 rounded h-3 w-12"></div>
            <div className="animate-pulse bg-gray-200 rounded h-3 w-12"></div>
            <div className="animate-pulse bg-gray-200 rounded h-3 w-12"></div>
            <div className="animate-pulse bg-gray-200 rounded h-3 w-12"></div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-center">
        <div className="inline-flex items-center px-3 py-1 bg-blue-100 rounded-full">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
          <span className="text-blue-600 text-xs">טוען נתונים...</span>
        </div>
      </div>
    </div>
  </div>
);

// 🚀 טבלה עם Lazy Loading ו-LeadModal
const LazyComparisonTable = ({ 
  category = 'study-funds', 
  specialization = 'כללי',
  title = 'קרנות השתלמות - כללי',
  fundIds = [],
  delay = 0,
  preload = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('year1');
  const [sortOrder, setSortOrder] = useState('desc');
  const [reportPeriod, setReportPeriod] = useState(null);
  const [description, setDescription] = useState('');
  const [tooltip, setTooltip] = useState({ show: false, id: null, position: { x: 0, y: 0 } });

  // 🆕 State למודל הליד
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [currentLeadContext, setCurrentLeadContext] = useState(null);

  const tableRef = useRef(null);
  const navigate = useNavigate();
  // פונקציית צילום מסך של הטבלה
// פונקציית צילום מסך של הטבלה - מתוקנת
// פונקציית צילום מסך של הטבלה - מתוקנת
const exportAsImage = async () => {
  if (!tableRef.current) {
    alert('שגיאה בטעינת הטבלה');
    return;
  }

  try {
    // הסתר רק את כפתורי הייצוא בכותרת הכחולה (לא את כפתורי המיון!)
    const exportButtonsContainer = tableRef.current.querySelector('.export-buttons-container');
    const mainActionButton = tableRef.current.querySelector('.main-action-button');
    const bottomActionButton = tableRef.current.querySelector('.bottom-action-button');
    const descriptionElement = tableRef.current.querySelector('.table-description'); // ← הוסף זה
    
    // שמור את המצב המקורי
    const originalStyles = [];
    
    if (exportButtonsContainer) {
      originalStyles.push({ element: exportButtonsContainer, display: exportButtonsContainer.style.display });
      exportButtonsContainer.style.display = 'none';
    }
    
    if (mainActionButton) {
      originalStyles.push({ element: mainActionButton, display: mainActionButton.style.display });
      mainActionButton.style.display = 'none';
    }
    
    if (bottomActionButton) {
      originalStyles.push({ element: bottomActionButton, display: bottomActionButton.style.display });
      bottomActionButton.style.display = 'none';
    }

    // ← הסתר את ה-description
    if (descriptionElement) {
      originalStyles.push({ element: descriptionElement, display: descriptionElement.style.display });
      descriptionElement.style.display = 'none';
    }

    // צלם את הטבלה
    const canvas = await html2canvas(tableRef.current, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true
    });

    // החזר את הכפתורים למצב המקורי
    originalStyles.forEach(({ element, display }) => {
      element.style.display = display;
    });

    // המר לתמונה והורד
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = `${title.replace(/[^a-zA-Z0-9\u0590-\u05FF]/g, '_')}_${new Date().toISOString().split('T')[0]}.png`;
      
      link.href = url;
      link.download = fileName;
      link.click();
      
      URL.revokeObjectURL(url);
    });

  } catch (error) {
    console.error('שגיאה בייצוא תמונה:', error);
    alert('אירעה שגיאה בהורדת התמונה');
  }
};
const exportToExcel = () => {
  if (sortedProducts.length === 0) {
    alert('אין נתונים לייצוא');
    return;
  }

  // הכנת הנתונים לייצוא
  const exportData = sortedProducts.map((product, index) => ({
    'דירוג': product.rank || '-',
    'שם הקרן': getDisplayName(product),
    'מזהה': product.id,
    [`תשואה ${getMonthName()} (%)`]: product.returns?.month1?.toFixed(2) || '-',
    'תשואה מתחילת השנה (%)': product.returns?.ytd?.toFixed(2) || '-',
    'תשואה שנה (%)': product.returns?.year1?.toFixed(2) || '-',
    'תשואה 3 שנים (%)': product.returns?.year3?.toFixed(2) || '-',
    'תשואה 5 שנים (%)': product.returns?.year5?.toFixed(2) || '-',

   
  }));

  // יצירת קובץ CSV
  const headers = Object.keys(exportData[0]);
  const csvContent = [
    headers.join(','),
    ...exportData.map(row => 
      headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && (value.includes(',') || value.includes('"')) 
          ? `"${value.replace(/"/g, '""')}"` 
          : value;
      }).join(',')
    )
  ].join('\n');

  // הורדת הקובץ
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${title.replace(/[^a-zA-Z0-9\u0590-\u05FF]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// פונקציית ייצוא ל-PDF
const exportToPDF = () => {
  if (sortedProducts.length === 0) {
    alert('אין נתונים לייצוא');
    return;
  }

  const printContent = `
    <!DOCTYPE html>
    <html dir="rtl" lang="he">
    <head>
      <meta charset="utf-8">
      <title>${title} - דוח השוואה</title>
      <style>
        @page {
          size: A4 landscape;
          margin: 15mm;
        }
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 10px;
          line-height: 1.2;
          margin: 0;
          direction: rtl;
        }
        .header {
          text-align: center;
          margin-bottom: 20px;
          border-bottom: 2px solid #3B82F6;
          padding-bottom: 10px;
        }
        .header h1 {
          margin: 0 0 10px 0;
          font-size: 18px;
          color: #1F2937;
        }
        .header p {
          margin: 5px 0;
          color: #6B7280;
          font-size: 12px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 15px;
          font-size: 9px;
        }
        th, td {
          border: 1px solid #E5E7EB;
          padding: 6px 4px;
          text-align: center;
          vertical-align: middle;
        }
        th {
          background-color: #F3F4F6;
          font-weight: bold;
          color: #374151;
        }
        .fund-name {
          text-align: right !important;
          padding: 6px 8px;
          max-width: 200px;
        }
        .fund-name .name {
          font-weight: bold;
          font-size: 9px;
          color: #1F2937;
          margin-bottom: 2px;
        }
        .fund-name .id {
          font-size: 8px;
          color: #3B82F6;
          background: #EBF4FF;
          padding: 2px 4px;
          border-radius: 4px;
        }
        .rank-1 {
          background-color: #059669 !important;
          color: white !important;
          font-weight: bold;
        }
        .rank-last {
          background-color: #DC2626 !important;
          color: white !important;
          font-weight: bold;
        }
        .positive { 
          color: #059669; 
          font-weight: bold;
        }
        .negative { 
          color: #DC2626; 
          font-weight: bold;
        }
        .neutral {
          color: #6B7280;
        }
        tr:nth-child(even) {
          background-color: #F9FAFB;
        }
        .summary {
          margin-top: 15px;
          padding: 10px;
          background: #F3F4F6;
          border-radius: 8px;
          font-size: 8px;
          page-break-inside: avoid;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${title}</h1>
        <p>דוח השוואה מפורט | תאריך: ${new Date().toLocaleDateString('he-IL')} | מספר קרנות: ${sortedProducts.length}</p>
        ${reportPeriod ? `<p>תקופת דיווח: ${reportPeriod}</p>` : ''}
        ${description ? `<p>${description}</p>` : ''}
      </div>
      
      <table>
        <thead>
          <tr>
            <th style="width: 8%;">דירוג</th>
            <th style="width: 25%;">פרטי הקרן</th>
            <th style="width: 10%;">${getMonthName()}<br>(%)</th>
            <th style="width: 12%;">מתחילת<br>השנה (%)</th>
            <th style="width: 10%;">שנה<br>(%)</th>
            <th style="width: 12%;">3 שנים<br>(%)</th>
            <th style="width: 12%;">5 שנים<br>(%)</th>
           
          </tr>
        </thead>
        <tbody>
          ${sortedProducts.map((product) => {
            const isFirst = product.rank === 1;
            const isLast = product.rank === sortedProducts.filter(p => p.rank).length;
            const rowClass = isFirst ? 'rank-1' : isLast ? 'rank-last' : '';
            
            return `
              <tr class="${rowClass}">
                <td>
                  ${product.rank || '-'}
                </td>
                <td class="fund-name">
                  <div class="name">${getDisplayName(product)}</div>
                  <div class="id">מזהה: ${product.id}</div>
                </td>
                <td class="${(product.returns?.month1 || 0) >= 0 ? 'positive' : (product.returns?.month1 || 0) < 0 ? 'negative' : 'neutral'}">
                  ${product.returns?.month1?.toFixed(2) || '-'}
                </td>
                <td class="${(product.returns?.ytd || 0) >= 0 ? 'positive' : (product.returns?.ytd || 0) < 0 ? 'negative' : 'neutral'}">
                  ${product.returns?.ytd?.toFixed(2) || '-'}
                </td>
                <td class="${(product.returns?.year1 || 0) >= 0 ? 'positive' : (product.returns?.year1 || 0) < 0 ? 'negative' : 'neutral'}">
                  ${product.returns?.year1?.toFixed(2) || '-'}
                </td>
                <td class="${(product.returns?.year3 || 0) >= 0 ? 'positive' : (product.returns?.year3 || 0) < 0 ? 'negative' : 'neutral'}">
                  ${product.returns?.year3?.toFixed(2) || '-'}
                </td>
                <td class="${(product.returns?.year5 || 0) >= 0 ? 'positive' : (product.returns?.year5 || 0) < 0 ? 'negative' : 'neutral'}">
                  ${product.returns?.year5?.toFixed(2) || '-'}
                </td>
            
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
      
      <div class="summary">
        <strong>הסברים:</strong><br>
        • דירוג מבוסס על ${sortBy === 'year1' ? 'תשואה שנתית' : sortBy === 'year3' ? 'תשואה 3 שנים' : sortBy === 'year5' ? 'תשואה 5 שנים' : sortBy === 'ytd' ? 'תשואה מתחילת השנה' : 'תשואה חודשית'}<br>
        • תשואות בצבע ירוק - חיוביות | תשואות בצבע אדום - שליליות<br>
        • מקום ראשון - רקע ירוק | מקום אחרון - רקע אדום<br>
        • ${specialization ? `התמחות: ${specialization}` : ''}<br>
        • דוח זה נוצר באמצעות מערכת גמל נט AI
      </div>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  printWindow.document.open();
  printWindow.document.write(printContent);
  printWindow.document.close();
  setTimeout(() => {
    printWindow.print();
  }, 500);
};

// 🆕 פונקציה לטיפול בליד - מעודכנת עם saveContact
const handleLeadSubmit = async (leadData) => {
  try {
   
    
    // המר את נתוני הליד לפורמט של saveContact
    const contactData = {
      name: leadData.name,
      email: '', // אין אימייל בליד
      phone: leadData.phone,
      subject: leadData.subject,
      message: leadData.message,
      category: leadData.category,
      source: leadData.source,
      sourceContext: leadData.sourceContext
    };
    
    // 🆕 שליחה אמיתית ל-Firebase
    const contactId = await saveContact(contactData);
   
    
    return true; // הצלחה
  } catch (error) {
    console.error('❌ Error submitting lead:', error);
    throw error;
  }
};
  // 🆕 פונקציה לפתיחת המודל עם קונטקסט
  const openLeadModal = (clickedProduct = null) => {
    const firstPlace = sortedProducts.find(p => p.rank === 1);
    
    const sourceContext = {
      source: 'comparison-table', // מציין שזה מטבלת השוואה רגילה
      category: category,
      specialization: specialization,
      currentSort: `${sortBy}_${sortOrder}`,
      userLocation: 'Israel',
      
      // אין מוצר נוכחי (כי זה לא דף מוצר)
      productId: null,
      productName: null,
      
      // פרטי המקום הראשון בטבלה
      firstPlace: firstPlace ? {
        id: firstPlace.id,
        name: getDisplayName(firstPlace),
        rank: firstPlace.rank,
        return: firstPlace.returns?.[sortBy] || 0
      } : null,
      
      // פרטי המוצר שלחץ עליו (אם רלוונטי)
      clickedProduct: clickedProduct ? {
        id: clickedProduct.id,
        name: getDisplayName(clickedProduct),
        rank: clickedProduct.rank
      } : null,
      
      // מידע נוסף על הטבלה
      tableInfo: {
        title: title,
        totalProducts: products.length,
        specialization: specialization
      }
    };

    setCurrentLeadContext(sourceContext);
    setIsLeadModalOpen(true);
  };

  // מצא את ה-description המתאים
  useEffect(() => {
    const specializations = getSpecializationConfigs(category);
    const matchingSpec = specializations.find(spec => spec.key === specialization);
    setDescription(matchingSpec?.description || '');
  }, [category, specialization]);

  // 🚀 טעינה מיד כשהקומפוננטה מוכנה
  useEffect(() => {
    // רק אם עדיין לא התחלנו תהליך טעינה
    if (isLoaded || loading || isVisible) return;
    
    // התחל תהליך טעינה מיד
    setIsVisible(true);
    
    // טען מיד ללא המתנה
    loadProducts();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundIds, category, title]);

  // טעינת נתונים (רק כשצריך)
  const loadProducts = async () => {
    if (isLoaded || loading) return;
 
    try {
      setLoading(true);
      setError(null);
      
      if (!fundIds || fundIds.length === 0) {
        setProducts([]);
        setIsLoaded(true);
        return;
      }

      // בדוק אם הקטגוריה כבר נטענה לcache
      const categoryKey = `category_${category}`;
      const categoryFromCache = api.cache.get(categoryKey);

      const filteredData = await api.getProductsByIds(fundIds, category);
      
      if (filteredData.length > 0 && filteredData[0].reportPeriod) {
        setReportPeriod(filteredData[0].reportPeriod);
      }
      
      // בדיקת קרנות חסרות - שיפור השוואת IDs
      const foundIdsSet = new Set(filteredData.map(p => {
        const id = p.id;
        // המרה ל-number אם אפשר, אחרת string
        const numId = parseInt(id);
        return isNaN(numId) ? String(id) : numId;
      }));
      
      const missingIds = fundIds.filter(id => {
        const numId = parseInt(id);
        const idToCheck = isNaN(numId) ? String(id) : numId;
        return !foundIdsSet.has(idToCheck);
      });
      
      if (missingIds.length > 0) {
        console.warn(`⚠️ Missing products for table "${title}": ${missingIds.join(', ')}`);
        console.log(`📊 Found ${filteredData.length} products, looking for ${fundIds.length} total`);
        
        // טעינה נוספת של קרנות חסרות
        const additionalProducts = [];
        for (const missingId of missingIds) {
          try {
            const missingProduct = await api.getProductById(missingId.toString(), category);
            if (missingProduct) {
              // בדיקה מדויקת שהקרן לא קיימת כבר
              const productId = missingProduct.id;
              const productNumId = parseInt(productId);
              const productIdToCheck = isNaN(productNumId) ? String(productId) : productNumId;
              
              const alreadyExists = foundIdsSet.has(productIdToCheck) || 
                                   filteredData.some(p => {
                                     const pId = parseInt(p.id);
                                     const pIdToCheck = isNaN(pId) ? String(p.id) : pId;
                                     return pIdToCheck === productIdToCheck;
                                   });
              
              if (!alreadyExists) {
                additionalProducts.push(missingProduct);
                foundIdsSet.add(productIdToCheck); // הוסף ל-set כדי למנוע כפילות
                if (!reportPeriod && missingProduct.reportPeriod) {
                  setReportPeriod(missingProduct.reportPeriod);
                }
              } else {
                console.log(`ℹ️ Product ${missingId} already exists, skipping duplicate load`);
              }
            }
          } catch (error) {
            console.warn(`❌ Failed to load product ${missingId}:`, error.message);
          }
        }
        
        if (additionalProducts.length > 0) {
          filteredData.push(...additionalProducts);
        }
      }

      // סינון כפילויות - רק ID ייחודי (שיפור השוואת IDs)
      const uniqueProducts = [];
      const seenIds = new Set();
      
      for (const product of filteredData) {
        const productId = product.id;
        const numId = parseInt(productId);
        const idToCheck = isNaN(numId) ? String(productId) : numId;
        
        if (!seenIds.has(idToCheck)) {
          seenIds.add(idToCheck);
          uniqueProducts.push(product);
        } else {
          console.warn(`⚠️ Duplicate product ID ${productId} found in table "${title}", removing duplicate`);
        }
      }

      console.log(`✅ Final products for table "${title}": ${uniqueProducts.length} unique products from ${fundIds.length} requested`);

      setProducts(uniqueProducts);

    } catch (err) {
      console.error(`Error loading table "${title}":`, err);
      setError(`שגיאה: ${err.message}`);
    } finally {
      setLoading(false);
      setIsLoaded(true);
    }
  };

  // טיפול בלחיצה על מוצר
  const handleProductClick = (product, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    navigate(`/product/${category}/${product.id}`);
  };

  // פונקציות מיון ותצוגה (זהות לטבלה המקורית)
  const getSortValue = (product, field) => {
    const safeValue = (value) => {
      if (value === null || value === undefined || isNaN(value)) return 0;
      return value;
    };
  
    switch (field) {
      case 'month1': return safeValue(product.returns?.month1);
      case 'ytd': return safeValue(product.returns?.ytd);
      case 'year1': return safeValue(product.returns?.year1);
      case 'year3': return safeValue(product.returns?.year3);
      case 'year5': return safeValue(product.returns?.year5);
      default: return 0;
    }
  };

  const displayValue = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }
    return value.toFixed(decimals) + '%';
  };

  const getMonthName = () => {
    const months = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
    
    if (reportPeriod) {
      const periodStr = reportPeriod.toString();
      if (periodStr.length === 6) {
        const monthNum = parseInt(periodStr.slice(-2));
        if (monthNum >= 1 && monthNum <= 12) {
          return months[monthNum - 1];
        }
      }
    }
    return months[new Date().getMonth()];
  };

  // מיון מוצרים
  const sortedProducts = React.useMemo(() => {
    if (!products.length) return [];

    const productsWithData = products.filter(product => {
      const hasValidData = product.returns && 
                          product.returns[sortBy] !== null && 
                          product.returns[sortBy] !== undefined && 
                          !isNaN(product.returns[sortBy]);
      return hasValidData;
    });

    const productsWithoutData = products.filter(product => {
      const hasValidData = product.returns && 
                          product.returns[sortBy] !== null && 
                          product.returns[sortBy] !== undefined && 
                          !isNaN(product.returns[sortBy]);
      return !hasValidData;
    });

    const sortedData = productsWithData.sort((a, b) => {
      const aValue = getSortValue(a, sortBy);
      const bValue = getSortValue(b, sortBy);
      return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    });

    const sortedWithData = sortedData.map((product, index) => ({
      ...product,
      rank: sortOrder === 'desc' ? index + 1 : productsWithData.length - index,
      hasDataForSort: true
    }));

    const withoutDataMapped = productsWithoutData.map(product => ({
      ...product,
      rank: null,
      hasDataForSort: false
    }));

    return [...sortedWithData, ...withoutDataMapped].slice(0, 10);
  }, [products, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'fee' ? 'asc' : 'desc');
    }
  };

  // פונקציות עיצוב (זהות למקור)
  const getRowClasses = (product, totalRanked) => {
    if (sortOrder === 'desc') {
      if (product.rank === 1) {
        return 'bg-green-600 text-white hover:bg-green-700';
      }
      if (product.rank && product.rank === totalRanked) {
        return 'bg-red-600 text-white hover:bg-red-700';
      }
    } else {
      if (product.rank === totalRanked) {
        return 'bg-red-600 text-white hover:bg-red-700';
      }
      if (product.rank === 1) {
        return 'bg-green-600 text-white hover:bg-green-700';
      }
    }
    return 'hover:bg-gray-50';
  };

  const getCellClasses = (field, product, value, totalRanked) => {
    const isActiveSort = sortBy === field;
    let baseClasses = 'px-1 py-2 text-center font-medium';
    
    if (isActiveSort && product.rank) {
      baseClasses += ' bg-green-100 text-green-800';
    } else {
      if (product.rank === 1 || (product.rank && product.rank === totalRanked)) {
        baseClasses += '';
      } else {
        if (value === null || value === undefined) {
          baseClasses += ' text-gray-400';
        } else if (field === 'month1') {
          baseClasses += (value || 0) >= 0 ? ' text-green-600' : ' text-red-600';
        } else if (field === 'ytd') {
          baseClasses += (value || 0) >= 0 ? ' text-blue-600' : ' text-red-600';
        } else if (field === 'year1') {
          baseClasses += (value || 0) >= 0 ? ' text-purple-600' : ' text-red-600';
        } else if (field === 'year3') {
          baseClasses += (value || 0) >= 0 ? ' text-indigo-600' : ' text-red-600';
        } else if (field === 'year5') {
          baseClasses += (value || 0) >= 0 ? ' text-pink-600' : ' text-red-600';
        }
      }
    }
    
    return baseClasses;
  };

  // תצוגה תלוית מצב
  
  // אם הטבלה עדיין לא נכנסה לתצוגה - הצג skeleton
  if (!isVisible) {
    return (
      <div ref={tableRef}>
        <TableSkeleton title={title} />
      </div>
    );
  }

  // אם יש שגיאה
  if (error) {
    return (
      <div ref={tableRef} className="bg-white rounded-lg shadow-md p-6 h-96 flex items-center justify-center">
        <div className="text-center text-red-500">
          <div className="text-sm mb-2">שגיאה: {error}</div>
          <button onClick={loadProducts} className="text-xs bg-red-600 text-white px-2 py-1 rounded">
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  // אם טוען - הצג skeleton עם אנימציה
  if (loading) {
    return (
      <div ref={tableRef}>
        <TableSkeleton title={title} />
      </div>
    );
  }

  // אם אין נתונים
  if (!fundIds || fundIds.length === 0 || sortedProducts.length === 0) {
    return (
      <div ref={tableRef} className="bg-white rounded-lg shadow-md p-6 h-96 flex items-center justify-center">
        <div className="text-center text-gray-500">
          <div className="text-sm mb-2">אין נתונים זמינים</div>
          <div className="text-xs">{title}</div>
        </div>
      </div>
    );
  }

  // טבלה מלאה עם נתונים
  const totalRanked = sortedProducts.filter(p => p.rank).length;

  return (
    <>
<div 
  ref={tableRef} 
  id={`table-${specialization}`}
  data-specialization={specialization}
  className="bg-white rounded-lg shadow-md overflow-hidden compact-table"
>
        {/* כותרת */}
    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3">
  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2 mb-3">
    <div>
      <h3 className="font-bold text-sm">{title}</h3>
      <div className="text-xs text-blue-100 mt-1">
        {fundIds && fundIds.length > 0 ? 
          `מבוסס על ${fundIds.length} קרנות הנבחרות והגדולות` : 
          'תשואות מצטברות לפני דמי ניהול • YTD ותשואה שנתית נפרדות'}
      </div>
    </div>

    <button
      onClick={() => openLeadModal()}
      className="
      main-action-button
        w-full md:w-auto
        bg-white text-blue-600 
        text-xs
        font-semibold 
        px-3 py-1.5
        rounded-full
        shadow-lg shadow-black/20 
        hover:bg-blue-50 active:bg-blue-100
        transition-all duration-200
        leading-tight
        flex items-center justify-center
        border-2 border-transparent hover:border-blue-200
        self-center md:self-auto
      "
    >
      <span className="text-center">
        הצעה מהירה ממתכנן פיננסי מוסמך (ללא עלות)
      </span>
    </button>
  </div>

  {/* כפתורי ייצוא */}
  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
    <div className="flex items-center gap-2">
      <span className="text-xs text-blue-100">מספר מוצרים: {sortedProducts.length}</span>
    </div>
    
<div className="flex items-center gap-2 export-buttons-container">
  <span className="text-xs text-blue-100">ייצא טבלה:</span>
  
  {/* כפתור תמונה חדש */}
  <button
    onClick={exportAsImage}
    className="flex items-center gap-1 px-2 py-1 bg-white/10 hover:bg-white/20 text-white rounded text-xs font-medium transition-colors"
    title="הורד כתמונה"
  >
    <svg 
      className="h-3 w-3" 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
      />
    </svg>
    <span className="hidden sm:inline">תמונה</span>
    <span className="sm:hidden">PNG</span>
  </button>
  
  <button
    onClick={exportToExcel}
    className="flex items-center gap-1 px-2 py-1 bg-white/10 hover:bg-white/20 text-white rounded text-xs font-medium transition-colors"
  >
    <FileSpreadsheet className="h-3 w-3" />
    <span className="hidden sm:inline">Excel</span>
    <span className="sm:hidden">XLS</span>
  </button>
  
  <button
    onClick={exportToPDF}
    className="flex items-center gap-1 px-2 py-1 bg-white/10 hover:bg-white/20 text-white rounded text-xs font-medium transition-colors"
  >
    <FileText className="h-3 w-3" />
    <span>PDF</span>
  </button>
</div>
  </div>
</div>

        {/* הטבלה */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-1 py-2 text-center font-extrabold text-gray-700">דירוג</th>
                <th className="px-2 py-2 text-right font-extrabold text-gray-700">שם</th>
                <th className="px-1 py-2 text-center">
                  <button 
                    onClick={() => handleSort('month1')}
                    className={`font-extrabold ${sortBy === 'month1' ? 'text-green-700 bg-green-100 rounded px-1' : 'text-gray-700 hover:text-blue-600'}`}
                  >
                    {getMonthName()}
                    {sortBy === 'month1' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                  </button>
                </th>
                <th className="px-1 py-2 text-center">
                  <button 
                    onClick={() => handleSort('ytd')}
                    className={`font-extrabold ${sortBy === 'ytd' ? 'text-green-700 bg-green-100 rounded px-1' : 'text-gray-700 hover:text-blue-600'}`}
                  >
                   מתחילת שנה
                    {sortBy === 'ytd' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                  </button>
                </th>
                <th className="px-1 py-2 text-center">
                  <button 
                    onClick={() => handleSort('year1')}
                    className={`font-extrabold ${sortBy === 'year1' ? 'text-green-700 bg-green-100 rounded px-1' : 'text-gray-700 hover:text-blue-600'}`}
                  >
                    שנה
                    {sortBy === 'year1' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                  </button>
                </th>
                <th className="px-1 py-2 text-center">
                  <button 
                    onClick={() => handleSort('year3')}
                    className={`font-extrabold ${sortBy === 'year3' ? 'text-green-700 bg-green-100 rounded px-1' : 'text-gray-700 hover:text-blue-600'}`}
                  >
                    3 שנים
                    {sortBy === 'year3' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                  </button>
                </th>
                <th className="px-1 py-2 text-center">
                  <button 
                    onClick={() => handleSort('year5')}
                    className={`font-extrabold ${sortBy === 'year5' ? 'text-green-700 bg-green-100 rounded px-1' : 'text-gray-700 hover:text-blue-600'}`}
                  >
                    5 שנים
                    {sortBy === 'year5' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                  </button>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {sortedProducts.map((product, index) => (
                <tr 
                  key={product.id} 
                  className={`${getRowClasses(product, totalRanked)} h-10 md:h-auto`}
                >
                  {/* דירוג */}
                 {/* דירוג */}
<td className="px-1 py-1 md:py-2 text-center">
  {product.rank ? (
    <span className="font-extrabold flex items-center justify-center gap-1">
      {sortOrder === 'desc' ? (
        product.rank === 1 ? (
          <img 
            src="/golden-bull.png" 
            alt="שור זהב" 
            className="w-8 h-8 md:w-12 md:h-12 object-contain zoom-pulse hover:scale-150 transition-transform duration-500 drop-shadow-lg filter brightness-110 cursor-pointer" 
          />
        ) : product.rank === totalRanked && product.rank >= 2 && product.rank <= 10 ? (
          <img 
            src={`/ScaredBear${totalRanked}.png`}
            alt="דב מפוחד" 
            className="w-8 h-8 md:w-12 md:h-12 object-contain zoom-pulse hover:scale-150 transition-transform duration-500 drop-shadow-lg cursor-pointer" 
          />
        ) : product.rank >= 2 && product.rank <= 10 ? (
          <span className="text-sm md:text-lg font-bold">{product.rank}</span>
        ) : (
          <img 
            src="/fightBearBull.png" 
            alt="קרב דב שור" 
            className="w-8 h-8 md:w-12 md:h-12 object-contain zoom-pulse hover:scale-150 transition-transform duration-500 drop-shadow-lg cursor-pointer" 
          />
        )
      ) : (
        product.rank === totalRanked && product.rank >= 2 && product.rank <= 10 ? (
          <img 
            src={`/ScaredBear${totalRanked}.png`}
            alt="דב מפוחד" 
            className="w-8 h-8 md:w-12 md:h-12 object-contain zoom-pulse hover:scale-150 transition-transform duration-500 drop-shadow-lg cursor-pointer" 
          />
        ) : product.rank === 1 ? (
          <img 
            src="/golden-bull.png" 
            alt="שור זהב" 
            className="w-8 h-8 md:w-12 md:h-12 object-contain zoom-pulse hover:scale-150 transition-transform duration-500 drop-shadow-lg filter brightness-110 cursor-pointer" 
          />
        ) : product.rank >= 2 && product.rank <= 10 ? (
          <span className="text-sm md:text-lg font-bold">{product.rank}</span>
        ) : (
          <img 
            src="/fightBearBull.png" 
            alt="קרב דב שור" 
            className="w-8 h-8 md:w-12 md:h-12 object-contain zoom-pulse hover:scale-150 transition-transform duration-500 drop-shadow-lg cursor-pointer" 
          />
        )
      )}
    </span>
  ) : (
    <span className="text-gray-300">-</span>
  )}
</td>

                  {/* שם המוצר - עם tooltip */}
                  <td className="px-1 md:px-2 py-1 md:py-2">
                    <a
                      href={`/product/${category}/${product.id}`}
                      onClick={(e) => handleProductClick(product, e)}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          show: true,
                          id: product.id,
                          position: {
                            x: rect.left + rect.width / 2,
                            y: rect.top - 10
                          }
                        });
                      }}
                      onMouseLeave={() => setTooltip({ show: false, id: null, position: { x: 0, y: 0 } })}
                      className="text-right hover:text-blue-300 text-xs no-underline block"
                    >
                      <div className="font-medium text-[10px] md:text-xs leading-tight max-w-20 md:max-w-32">
                        {getDisplayName(product)}
                      </div>
                    </a>
                  </td>
                  
                  {/* תאי התשואות - מותאמים למובייל */}
                  <td className={`${getCellClasses('month1', product, product.returns?.month1, totalRanked)} px-1 py-1 md:py-2`}>
                    <span className="text-xs md:text-sm">
                      {displayValue(product.returns?.month1)}
                    </span>
                  </td>

                  <td className={`${getCellClasses('ytd', product, product.returns?.ytd, totalRanked)} px-1 py-1 md:py-2`}>
                    <span className="text-xs md:text-sm">
                      {displayValue(product.returns?.ytd)}
                    </span>
                  </td>

                  <td className={`${getCellClasses('year1', product, product.returns?.year1, totalRanked)} px-1 py-1 md:py-2`}>
                    <span className="text-xs md:text-sm">
                      {displayValue(product.returns?.year1)}
                    </span>
                  </td>

                  <td className={`${getCellClasses('year3', product, product.returns?.year3, totalRanked)} px-1 py-1 md:py-2`}>
                    <span className="text-xs md:text-sm">
                      {displayValue(product.returns?.year3)}
                    </span>
                  </td>

                  <td className={`${getCellClasses('year5', product, product.returns?.year5, totalRanked)} px-1 py-1 md:py-2`}>
                    <span className="text-xs md:text-sm">
                      {displayValue(product.returns?.year5)}
                    </span>
                  </td>
                </tr>
              ))}

              {/* שורת ממוצע קבוצה */}
              <tr className="bg-gradient-to-r from-blue-50 to-purple-50 border-t-2 border-blue-300 font-bold">
                <td colSpan="2" className="px-2 py-2 text-right text-xs md:text-sm">
                  📊 ממוצע קבוצה
                </td>
                <td className="px-1 py-2 text-center text-xs md:text-sm">
                  {(() => {
                    const values = sortedProducts
                      .map(p => p.returns?.month1)
                      .filter(v => v !== null && v !== undefined && !isNaN(v));
                    return values.length > 0 
                      ? (values.reduce((sum, v) => sum + v, 0) / values.length).toFixed(2) + '%'
                      : '-';
                  })()}
                </td>
                <td className="px-1 py-2 text-center text-xs md:text-sm">
                  {(() => {
                    const values = sortedProducts
                      .map(p => p.returns?.ytd)
                      .filter(v => v !== null && v !== undefined && !isNaN(v));
                    return values.length > 0 
                      ? (values.reduce((sum, v) => sum + v, 0) / values.length).toFixed(2) + '%'
                      : '-';
                  })()}
                </td>
                <td className="px-1 py-2 text-center text-xs md:text-sm">
                  {(() => {
                    const values = sortedProducts
                      .map(p => p.returns?.year1)
                      .filter(v => v !== null && v !== undefined && !isNaN(v));
                    return values.length > 0 
                      ? (values.reduce((sum, v) => sum + v, 0) / values.length).toFixed(2) + '%'
                      : '-';
                  })()}
                </td>
                <td className="px-1 py-2 text-center text-xs md:text-sm">
                  {(() => {
                    const values = sortedProducts
                      .map(p => p.returns?.year3)
                      .filter(v => v !== null && v !== undefined && !isNaN(v));
                    return values.length > 0 
                      ? (values.reduce((sum, v) => sum + v, 0) / values.length).toFixed(2) + '%'
                      : '-';
                  })()}
                </td>
                <td className="px-1 py-2 text-center text-xs md:text-sm">
                  {(() => {
                    const values = sortedProducts
                      .map(p => p.returns?.year5)
                      .filter(v => v !== null && v !== undefined && !isNaN(v));
                    return values.length > 0 
                      ? (values.reduce((sum, v) => sum + v, 0) / values.length).toFixed(2) + '%'
                      : '-';
                  })()}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Tooltip */}
          {tooltip.show && (
            <div
              className="fixed z-[9999] pointer-events-none transform -translate-x-1/2 -translate-y-full"
              style={{
                left: tooltip.position.x,
                top: tooltip.position.y
              }}
            >
              <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                <div className="font-semibold">מספר באוצר: {tooltip.id}</div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          )}
        </div>

        {/* הוספת ה-description מתחת לטבלה */}
    {description && (
  <div className="table-description text-center text-sm text-gray-600 mt-4 mb-4 px-4">
    {description}
  </div>
)}
        
       <style>{`
          @keyframes zoomPulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.3);
            }
          }
          
          .zoom-pulse {
            animation: zoomPulse 2s infinite ease-in-out;
          }
        `}</style>
        
        {/* 🆕 כפתור מעודכן שפותח את המודל */}
        <div className="bg-green-50 p-2 text-center">
   <button
  onClick={() => openLeadModal()}
  className="group bottom-action-button relative overflow-hidden bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 hover:from-pink-600 hover:via-purple-600 hover:to-blue-600 text-white px-3 py-1 rounded-lg text-xs font-bold inline-flex items-center gap-1 shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300"
>
  {/* רקע זוהר קטן */}
  <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-lg blur opacity-20 group-hover:opacity-40 animate-pulse -z-10"></div>
  
  {/* תוכן הכפתור הקטן */}
  <div className="relative flex items-center gap-1">
    <TrendingUp className="w-3 h-3 animate-pulse group-hover:animate-bounce transition-all duration-300" />
    <span className="bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent font-bold">
      קח אותי לקרן המובילה
    </span>
  </div>
</button>
        </div>
      </div>

      {/* 🆕 המודל */}
      <LeadModal
        isOpen={isLeadModalOpen}
        onClose={() => setIsLeadModalOpen(false)}
        onSubmit={handleLeadSubmit}
        sourceContext={currentLeadContext}
      />
    </>
  );
};

export default LazyComparisonTable;